import React, { useEffect, useState } from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';
import { FormSection } from '../ui/FormSection';
import { Button } from '../ui/Button';
import { ConfirmationModal } from '../ui/ConfirmationModal';
import { FormErrorMessage } from '../ui/FormErrorMessage';
import { FormSuccessMessage } from '../ui/FormSuccessMessage';
import { useTheme } from '../../contexts/ThemeContext';
import { isNativeApp } from '../../utils/platform';

export interface AccountDeletionSectionProps {
  onDeleteAccount: (password: string, captchaToken: string) => Promise<void>;
  isSubmitting?: boolean;
  error?: string | null;
  success?: string | null;
  validationError?: string | null;
  className?: string;
}

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY ?? '0x4AAAAAAB9PVF5WNM2_V9pV';

/**
 * Account deletion section with warning UI and confirmation modal
 * Implements password verification for deletion confirmation
 * Requirements: 4.1, 4.2, 4.3
 */
export const AccountDeletionSection: React.FC<AccountDeletionSectionProps> = ({
  onDeleteAccount,
  isSubmitting = false,
  error,
  success,
  validationError,
  className
}) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaResetCounter, setCaptchaResetCounter] = useState(0);
  const { theme } = useTheme();
  const skipCaptcha = isNativeApp();

  useEffect(() => {
    if (!showConfirmModal) {
      setCaptchaToken(null);
      setCaptchaResetCounter(prev => prev + 1);
    }
  }, [showConfirmModal]);

  const handleDeleteClick = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmDeletion = async (password?: string) => {
    if (!password || (!skipCaptcha && !captchaToken)) {
      return;
    }
    try {
      await onDeleteAccount(password, captchaToken || '');
      setShowConfirmModal(false);
    } catch (err) {
      // Error handling is managed by parent component
      // Modal stays open to allow retry
    } finally {
      setCaptchaToken(null);
      setCaptchaResetCounter(prev => prev + 1);
    }
  };

  const handleCloseModal = () => {
    if (!isSubmitting) {
      setShowConfirmModal(false);
    }
  };

  return (
    <>
      <FormSection
        title="Delete Account"
        description="Permanently delete your account and all associated data. This action cannot be undone."
        variant="danger"
        icon={<AlertTriangle className="w-4 h-4" />}
        className={className}
      >
        {/* Warning Content */}
        <div className="space-y-3">
          <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-3 sm:p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="space-y-2 min-w-0 flex-1">
                <h4 className="text-sm font-medium text-red-900 dark:text-red-100">
                  Warning: This action is permanent
                </h4>
                <ul className="text-xs sm:text-sm text-red-700 dark:text-red-300 space-y-1">
                  <li>• Your account will be permanently deleted</li>
                  <li>• All your game progress and statistics will be lost</li>
                  <li>• Your leaderboard entries will be removed</li>
                  <li>• This action cannot be undone or recovered</li>
                  <li>• You will be immediately signed out</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Data Retention Notice */}
          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <p className="text-xs sm:text-sm text-amber-700 dark:text-amber-300 leading-relaxed">
              <strong>Data Retention:</strong> Some data may be retained for legal and security purposes 
              as outlined in our Privacy Policy. Personal identifiers will be removed.
            </p>
          </div>

          {/* Error and Success Messages */}
          {error && (
            <FormErrorMessage error={error} />
          )}
          
          {success && (
            <FormSuccessMessage message={success} />
          )}

          {/* Delete Button */}
          <div className="pt-2">
            <Button
              variant="destructive"
              onClick={handleDeleteClick}
              disabled={isSubmitting}
              className="w-full sm:w-auto mobile-touch-target text-sm sm:text-base"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Processing Deletion...' : 'Delete My Account'}
            </Button>
          </div>
        </div>
      </FormSection>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDeletion}
        title="Confirm Account Deletion"
        description="Are you absolutely sure you want to delete your account? This action is permanent and cannot be undone. All your data will be lost forever."
        confirmText="Yes, Delete My Account"
        cancelText="Cancel"
        variant="danger"
        requirePassword={true}
        isSubmitting={isSubmitting}
        confirmDisabled={!skipCaptcha && !captchaToken}
        extraContent={
          <div className="space-y-2">
            {validationError && (
              <p className="text-sm text-red-600 dark:text-red-400">
                {validationError}
              </p>
            )}
            {!skipCaptcha && (
              <div className="flex justify-center">
                <Turnstile
                  key={captchaResetCounter}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={(token) => setCaptchaToken(token)}
                  onError={() => setCaptchaToken(null)}
                  onExpire={() => setCaptchaToken(null)}
                  options={{ theme: theme === 'dark' ? 'dark' : 'light' }}
                />
              </div>
            )}
          </div>
        }
      />
    </>
  );
};
